/**
 * iurop-marquee — shared infinite ticker for the trust bar and the logo bar.
 *
 * Drives the track with `transform: translateX()` on a rAF loop (GPU-composited,
 * sub-pixel smooth — no scrollLeft integer stutter) and wraps the offset modulo
 * one group's pixel width, so the loop is genuinely seamless (no flicker at the
 * seam). The track holds two identical groups, so at any offset in
 * [0, groupWidth) the viewport is always fully covered.
 *
 * It is ALSO draggable with the finger: `touch-action: pan-y` lets a vertical
 * swipe scroll the page while horizontal gestures are delivered to us as a drag.
 * Releasing a drag adds a little momentum, then the auto-advance resumes.
 *
 * Only runs where the marquee layout is active (mobile, <= 750px). On desktop
 * the bars are a static centred row, so the script stays idle there. If the JS
 * never loads, the CSS keyframe animation is the graceful fallback.
 */
(function () {
  'use strict';

  var MOBILE_MQ = '(max-width: 750px)';
  var REDUCE_MQ = '(prefers-reduced-motion: reduce)';
  var DRAG_THRESHOLD = 6; // px before a press counts as a drag (vs a tap/click)
  var MAX_FLING = 2.5; // cap release velocity (px per ms)

  function Marquee(section) {
    this.section = section;
    this.track = section.querySelector('[data-marquee-track]');
    if (!this.track) return;
    this.viewport = this.track.parentElement;

    this.speed = parseFloat(section.getAttribute('data-speed')) || 20; // seconds per group
    this.offset = 0;
    this.groupWidth = 0;
    this.rafId = null;
    this.lastTs = 0;

    this.isMobile = false;
    this.isVisible = true;

    this.dragging = false;
    this.moved = false;
    this.startX = 0;
    this.startOffset = 0;
    this.lastX = 0;
    this.lastMoveTs = 0;
    this.velocity = 0; // px per ms

    this.mqlMobile = window.matchMedia(MOBILE_MQ);
    this.mqlReduce = window.matchMedia(REDUCE_MQ);

    this.onFrame = this.onFrame.bind(this);
    this.onDown = this.onDown.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);
    this.onClick = this.onClick.bind(this);
    this.measure = this.measure.bind(this);
    this.evaluateMQ = this.evaluateMQ.bind(this);

    // Re-measure when the viewport resizes or lazy images change the width.
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(this.measure);
      this.ro.observe(this.track);
    }
    window.addEventListener('resize', this.measure);

    // Pause the loop while the bar is off-screen (battery).
    if ('IntersectionObserver' in window) {
      var self = this;
      this.io = new IntersectionObserver(function (entries) {
        self.isVisible = entries[0].isIntersecting;
        self.updateLoop();
      });
      this.io.observe(this.section);
    }

    addMQListener(this.mqlMobile, this.evaluateMQ);
    this.evaluateMQ();
  }

  Marquee.prototype.measure = function () {
    var group = this.track.firstElementChild;
    this.groupWidth = group ? group.getBoundingClientRect().width : 0;
  };

  Marquee.prototype.evaluateMQ = function () {
    this.setMobile(this.mqlMobile.matches);
  };

  Marquee.prototype.setMobile = function (on) {
    if (on === this.isMobile) return;
    this.isMobile = on;
    if (on) {
      this.measure();
      // JS drives the transform now — silence the CSS fallback animation.
      this.track.style.animation = 'none';
      this.apply();
      this.viewport.addEventListener('pointerdown', this.onDown);
      this.viewport.addEventListener('click', this.onClick, true);
    } else {
      this.endDrag();
      this.velocity = 0;
      this.offset = 0;
      this.viewport.removeEventListener('pointerdown', this.onDown);
      this.viewport.removeEventListener('click', this.onClick, true);
      this.track.style.animation = '';
      this.track.style.transform = '';
    }
    this.updateLoop();
  };

  Marquee.prototype.updateLoop = function () {
    if (this.isMobile && this.isVisible) {
      if (!this.rafId) {
        this.lastTs = 0;
        this.rafId = requestAnimationFrame(this.onFrame);
      }
    } else if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  };

  Marquee.prototype.apply = function () {
    if (this.groupWidth > 0) {
      this.offset = ((this.offset % this.groupWidth) + this.groupWidth) % this.groupWidth;
    }
    this.track.style.transform = 'translateX(' + -this.offset + 'px)';
  };

  Marquee.prototype.onFrame = function (ts) {
    if (!this.lastTs) this.lastTs = ts;
    var dt = ts - this.lastTs;
    this.lastTs = ts;
    if (dt > 64) dt = 64; // clamp after a tab/throttle pause

    if (this.dragging) {
      // Position is set directly by the pointer handlers.
    } else if (this.velocity !== 0) {
      this.offset += this.velocity * dt;
      this.velocity *= Math.pow(0.95, dt / 16.67); // friction
      if (Math.abs(this.velocity) < 0.004) this.velocity = 0;
      this.apply();
    } else if (!this.mqlReduce.matches && this.groupWidth > 0) {
      var pxPerMs = this.groupWidth / this.speed / 1000;
      this.offset += pxPerMs * dt;
      this.apply();
    }

    this.rafId = requestAnimationFrame(this.onFrame);
  };

  Marquee.prototype.onDown = function (e) {
    if (e.button != null && e.button !== 0) return;
    this.dragging = true;
    this.moved = false;
    this.velocity = 0;
    this.startX = e.clientX;
    this.startOffset = this.offset;
    this.lastX = e.clientX;
    this.lastMoveTs = e.timeStamp;
    try { this.viewport.setPointerCapture(e.pointerId); } catch (err) {}
    this.viewport.addEventListener('pointermove', this.onMove);
    this.viewport.addEventListener('pointerup', this.onUp);
    this.viewport.addEventListener('pointercancel', this.onUp);
  };

  Marquee.prototype.onMove = function (e) {
    if (!this.dragging) return;
    var dx = e.clientX - this.startX;
    if (Math.abs(dx) > DRAG_THRESHOLD) this.moved = true;
    this.offset = this.startOffset - dx; // content follows the finger
    var dt = e.timeStamp - this.lastMoveTs;
    if (dt > 0) this.velocity = (this.lastX - e.clientX) / dt;
    this.lastX = e.clientX;
    this.lastMoveTs = e.timeStamp;
    this.apply();
  };

  Marquee.prototype.onUp = function (e) {
    if (!this.dragging) return;
    try { this.viewport.releasePointerCapture(e.pointerId); } catch (err) {}
    // If the pointer went idle before release, don't fling.
    if (e.timeStamp - this.lastMoveTs > 80) this.velocity = 0;
    if (this.velocity > MAX_FLING) this.velocity = MAX_FLING;
    if (this.velocity < -MAX_FLING) this.velocity = -MAX_FLING;
    this.endDrag();
  };

  Marquee.prototype.endDrag = function () {
    if (!this.dragging) return;
    this.dragging = false;
    this.viewport.removeEventListener('pointermove', this.onMove);
    this.viewport.removeEventListener('pointerup', this.onUp);
    this.viewport.removeEventListener('pointercancel', this.onUp);
  };

  // Swallow the click that ends a drag so logo links don't navigate on release.
  Marquee.prototype.onClick = function (e) {
    if (this.moved) {
      e.preventDefault();
      e.stopPropagation();
      this.moved = false;
    }
  };

  function addMQListener(mql, fn) {
    if (mql.addEventListener) mql.addEventListener('change', fn);
    else if (mql.addListener) mql.addListener(fn);
  }

  function init() {
    var sections = document.querySelectorAll('[data-marquee]');
    for (var i = 0; i < sections.length; i++) new Marquee(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
